import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, User, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setPost(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-golden-2xl flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-golden-2xl flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl mb-golden-md">Artículo no encontrado</h2>
          <Link to="/blog" className="btn-primary inline-flex"><ArrowLeft className="w-5 h-5" /> Volver al blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl">
      <article className="max-w-3xl mx-auto px-golden-md lg:px-golden-lg">
        <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-primary-dim transition-colors mb-golden-xl" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Volver al blog
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-golden-md bg-primary/10 text-primary">
            {post.categoria}
          </span>
          <h1 className="text-3xl lg:text-4xl mb-golden-lg leading-tight" data-testid="blog-post-title">{post.titulo}</h1>
          <div className="flex items-center gap-golden-lg text-text-muted text-sm mb-golden-xl border-b border-border-subtle pb-golden-lg">
            <span className="flex items-center gap-1"><User className="w-4 h-4" /> {post.autor}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {post.fecha?.split('T')[0]}</span>
            <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {post.vistas} vistas</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none" data-testid="blog-post-content">
          {post.contenido?.split('\n').map((paragraph, i) => {
            if (paragraph.startsWith('## ')) {
              return <h2 key={i} className="text-2xl font-bold text-text-heading mt-golden-xl mb-golden-md">{paragraph.replace('## ', '')}</h2>;
            }
            if (paragraph.startsWith('### ')) {
              return <h3 key={i} className="text-xl font-semibold text-text-heading mt-golden-lg mb-golden-sm">{paragraph.replace('### ', '')}</h3>;
            }
            if (paragraph.trim() === '') return null;
            return <p key={i} className="text-text-body mb-golden-md leading-relaxed">{paragraph}</p>;
          })}
        </motion.div>

        <div className="mt-golden-2xl border-t border-border-subtle pt-golden-xl">
          <div className="glass-card p-golden-xl text-center bg-gradient-to-br from-primary/5 to-secondary/5">
            <BookOpen className="w-8 h-8 text-primary mx-auto mb-golden-md" />
            <h3 className="text-xl mb-golden-md">¿Te ha resultado útil?</h3>
            <p className="text-text-body mb-golden-lg">Descubre cómo podemos ayudarte a implementar estas ideas en tu negocio.</p>
            <div className="flex flex-col sm:flex-row gap-golden-md justify-center">
              <Link to="/agenda" className="btn-primary inline-flex justify-center">Agendar consultoría estratégica</Link>
              <Link to="/blog" className="btn-secondary inline-flex justify-center">Seguir leyendo</Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
